/**
 * EATSANE — automatic gift (קרקר PRO GO)
 *
 * Keeps exactly one קרקר PRO GO in every cart that holds at least one other item.
 * The line carries a hidden `_auto_gift` property so the theme can badge it and so
 * we can tell our line apart from one the customer picked out themselves.
 *
 * Pricing is NOT handled here. An automatic "Buy X get Y" discount zeroes the line.
 * If that discount is off, the cracker is charged at full price — check both together.
 *
 * The cart is the customer's, not ours: every failure path leaves it untouched.
 */
(function () {
  const config = window.EATSANE_AUTO_GIFT;
  if (!config || !config.enabled || !config.variantId) return;

  const GIFT_ID = Number(config.variantId);
  const PROP = config.property || '_auto_gift';
  // Never leave a cracker the customer did not ask for on a line they have to pay for.
  const REQUIRE_FREE = config.requireFree !== false;
  const FLAG = 'yes';
  const OPTED_OUT = 'eatsane:auto-gift:opted-out';
  const SEEN = 'eatsane:auto-gift:seen';

  const routes = window.routes || {};
  const withJs = (url, fallback) => {
    const base = url || fallback;
    return base.endsWith('.js') ? base : base + '.js';
  };
  const CART_URL = withJs(routes.cart_url, '/cart');
  const ADD_URL = withJs(routes.cart_add_url, '/cart/add');
  const CHANGE_URL = withJs(routes.cart_change_url, '/cart/change');
  const UPDATE_URL = withJs(routes.cart_update_url, '/cart/update');

  const originalFetch = window.fetch.bind(window);
  let queue = Promise.resolve();

  // Private-mode browsers throw on sessionStorage rather than returning null.
  const flag = {
    get(key) {
      try {
        return sessionStorage.getItem(key) === '1';
      } catch (error) {
        return false;
      }
    },
    set(key, on) {
      try {
        on ? sessionStorage.setItem(key, '1') : sessionStorage.removeItem(key);
      } catch (error) {
        /* nothing we can do, and nothing worth breaking the cart over */
      }
    },
  };

  const isOurs = (item) => item.properties && item.properties[PROP] === FLAG;

  function decide(cart) {
    const items = (cart && cart.items) || [];
    const ours = items.find((item) => item.variant_id === GIFT_ID && isOurs(item));
    const theirs = items.find((item) => item.variant_id === GIFT_ID && !isOurs(item));
    const others = items.filter((item) => item.variant_id !== GIFT_ID);

    if (items.length === 0) {
      // Empty cart — forget that they ever opted out, so the next cart starts clean.
      flag.set(OPTED_OUT, false);
      flag.set(SEEN, false);
      return null;
    }

    if (others.length === 0) {
      // Crackers and nothing else. Ours has no purchase to accompany; one they
      // deliberately added stays exactly where it is.
      if (ours) {
        flag.set(SEEN, false);
        return { action: 'remove', key: ours.key };
      }
      return null;
    }

    // They already chose a cracker. The discount frees one per order, so a second
    // line would just be a cracker they have to pay for.
    if (theirs) return null;

    if (ours) {
      flag.set(SEEN, true);
      return ours.quantity > 1 ? { action: 'clamp', key: ours.key } : null;
    }

    // Real items, no gift. If we put one here earlier, the customer took it out.
    if (flag.get(SEEN)) {
      flag.set(OPTED_OUT, true);
      flag.set(SEEN, false);
      return null;
    }

    return flag.get(OPTED_OUT) ? null : { action: 'add' };
  }

  async function post(url, body) {
    const response = await originalFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    // Sold out, cart limits, anything else — leave the cart as the customer left it.
    return response.ok ? response.json() : null;
  }

  async function getCart() {
    const response = await originalFetch(CART_URL, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  function apply(plan, sections, sectionsUrl) {
    const body = {};
    if (sections) {
      body.sections = sections;
      body.sections_url = sectionsUrl || window.location.pathname;
    }

    if (plan.action === 'add') {
      body.items = [{ id: GIFT_ID, quantity: 1, properties: { [PROP]: FLAG } }];
      return post(ADD_URL, body).then((result) => {
        if (!result) return null;
        flag.set(SEEN, true);
        return REQUIRE_FREE ? withdrawUnlessFree(result) : result;
      });
    }

    body.id = plan.key;
    body.quantity = plan.action === 'clamp' ? 1 : 0;
    return post(CHANGE_URL, body);
  }

  /**
   * The gift is only free because an automatic "Buy X get Y" discount zeroes it.
   * If that discount is switched off, capped, or beaten by a discount it cannot
   * combine with, the customer would be charged for a cracker they never chose.
   * Rather than risk that, take it back out and stop offering it this session.
   */
  async function withdrawUnlessFree(addResult) {
    const cart = await getCart();
    const ours = (cart.items || []).find((item) => item.variant_id === GIFT_ID && isOurs(item));
    if (!ours) return null;
    if (ours.final_line_price === 0) return addResult;

    await post(CHANGE_URL, { id: ours.key, quantity: 0 });
    flag.set(SEEN, false);
    flag.set(OPTED_OUT, true);
    return null;
  }

  /**
   * Mirror whatever sections the caller asked the cart API to re-render, so our
   * follow-up request returns those same sections built from the final cart.
   */
  function readSections(body) {
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return { sections: body.get('sections'), sectionsUrl: body.get('sections_url') };
      }
      if (typeof body === 'string') {
        const parsed = JSON.parse(body);
        return { sections: parsed.sections, sectionsUrl: parsed.sections_url };
      }
    } catch (error) {
      /* fall through to no sections — the theme just repaints a beat later */
    }
    return { sections: null, sectionsUrl: null };
  }

  const asJson = (payload) =>
    new Response(JSON.stringify(payload), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });

  async function reconcile(response, requestBody, isAdd) {
    const plan = decide(await getCart());
    if (!plan) return response;

    const { sections, sectionsUrl } = readSections(requestBody);
    const result = await apply(plan, sections, sectionsUrl);
    if (!result || !sections) return response;

    if (isAdd) {
      // /cart/add returns the added line, and the theme only reads `sections` and
      // `id` off it. Swapping in sections rendered after the gift landed means the
      // theme paints once, already correct, instead of flashing the cart twice.
      const payload = await response.clone().json();
      payload.sections = result.sections;
      return asJson(payload);
    }

    // /cart/change and /cart/update hand the theme a full cart object it reads
    // item_count and items from, so a stale body would mis-render an emptied cart.
    // A no-op update re-reads the authoritative cart together with its sections.
    const cart = await post(UPDATE_URL, {
      updates: {},
      sections,
      sections_url: sectionsUrl || window.location.pathname,
    });
    return cart ? asJson(cart) : response;
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const cartWrite = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
    const request = originalFetch(input, init);
    if (!cartWrite) return request;

    const isAdd = /\/cart\/add/.test(url);
    const run = request.then((response) => {
      if (!response.ok) return response;
      return reconcile(response, init && init.body, isAdd).catch(() => response);
    });

    // Serialise our follow-up work so two quick clicks cannot race each other.
    queue = queue.then(() => run, () => run);
    return run;
  };

  async function onLoad() {
    const plan = decide(await getCart());
    if (!plan) return;

    const result = await apply(plan, 'cart-icon-bubble', window.location.pathname);
    if (!result) return;

    const markup = result.sections && result.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (markup && bubble) {
      const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }

    // Dawn's cart elements re-fetch their own section whenever they see a cart
    // update they did not cause, which covers both the drawer and the cart page.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'eatsane-auto-gift', cartData: result });
    }
  }

  const start = () => onLoad().catch(() => {});
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
